import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { notificationService, openNotificationSettings } from '@/lib/notifications';

// Unified permission type for the app
type AppPermission = 'granted' | 'denied' | 'default';

interface NotificationPermissionBannerProps {
  onDismiss?: () => void;
}

export function NotificationPermissionBanner({ onDismiss }: NotificationPermissionBannerProps) {
  const [permissionStatus, setPermissionStatus] = useState<AppPermission>('default');
  const [isRequesting, setIsRequesting] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const checkPermission = async () => {
      try {
        // Use storage-based permission check (fast, no bridge calls)
        const status = await notificationService.getStoredPermissionStatus();

        // Check if user has previously dismissed the banner
        const dismissed = localStorage.getItem('notification-banner-dismissed');

        // If permissions are granted, clear the dismissed state
        if (status === 'granted') {
          localStorage.removeItem('notification-banner-dismissed');
          setIsDismissed(false);
        } else {
          // If permissions are not granted, check dismissed state
          setIsDismissed(dismissed === 'true');
        }

        setPermissionStatus(status);
      } catch {
        // On storage/check error, don't show the banner (graceful degradation)
        setPermissionStatus('granted');
      }
    };

    // Set up listener for permission changes (when user returns from settings)
    const handlePermissionChange = async () => {
      await checkPermission();
    };

    // Check permissions immediately from storage (no delay needed)
    checkPermission();

    notificationService.addPermissionChangeListener(handlePermissionChange);

    // Cleanup on unmount
    return () => {
      notificationService.removePermissionChangeListener(handlePermissionChange);
    };
  }, []);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    try {
      // Use native permission request (no custom dialog)
      const token = await notificationService.requestPermissionsAndInitialize();
      
      if (token) {
        setPermissionStatus('granted');
        setIsDismissed(true);
        // Clear the dismissed state since permissions are now granted
        localStorage.removeItem('notification-banner-dismissed');
        onDismiss?.();
      } else {
        setPermissionStatus('denied');
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      setPermissionStatus('denied');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('notification-banner-dismissed', 'true');
    onDismiss?.();
  };

  const handleOpenSettings = async () => {
    try {
      await openNotificationSettings();
    } catch (error) {
      console.error('Failed to open app settings:', error);
    }
  };

  // Show banner if:
  // - Permission is denied (always show, regardless of dismissal)
  // - Permission is default AND not dismissed
  // Don't show if permission is granted
  const shouldShow = permissionStatus === 'denied' || (permissionStatus === 'default' && !isDismissed);
  
  
  if (!shouldShow) {
    return null;
  }

  return (
    <div className="bg-red-100 border-b border-red-300 border-l-4 border-l-red-500 px-4 py-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div className="flex-shrink-0 flex items-center justify-center rounded-full bg-red-200 p-1.5 ring-2 ring-red-400/50">
            <Bell className="h-6 w-6 text-red-700" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-200 text-red-900">
                Required
              </span>
            </div>
            {permissionStatus === 'denied' ? (
              <p className="text-base text-red-900">
                <span className="font-semibold text-red-800">Notifications are disabled.</span>{' '}
                <span className="font-medium text-red-800">In order for this app to work properly</span> you need to allow notifications.{' '}
                <button
                  onClick={handleOpenSettings}
                  className="text-red-700 hover:text-red-900 underline font-semibold"
                >
                  Open settings to enable
                </button>
              </p>
            ) : (
              <p className="text-base text-red-900">
                <span className="font-medium text-red-800">In order for this app to work properly</span> you need to allow notifications.{' '}
                <button
                  onClick={handleRequestPermission}
                  disabled={isRequesting}
                  className="text-red-700 hover:text-red-900 underline font-semibold disabled:opacity-50"
                >
                  {isRequesting ? 'Requesting...' : 'Please click here to allow notifications'}
                </button>
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-red-500 hover:text-red-700 p-1 -m-1 rounded"
          aria-label="Dismiss notification banner"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
